import React, { useState, useCallback, useEffect } from 'react';
import Toolbar from 'src/components/Toolbar';
import { useTranslation } from 'react-i18next';
import GenderSelector from 'src/components/GenderSelector';
import DatePicker from 'src/components/InputDatePicker';
import Button from 'src/components/Button';
import { useHistory } from 'react-router-dom';
import TextInput from 'src/components/TextInput';
import forwardIcon from 'src/assets/forward_white.svg';
import NationSelector from 'src/components/NationSelector';
import PopupService from 'src/utils/PopupService';
import { BUYING_PROCESS, BUYING_PROCESS_STEP, BUYING_PROCESS_TRACKING, VN_COUNTRY_CODE } from 'src/constants';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import UserInfoService from 'src/utils/UserInfoService';
import { isValidDate, confirmQuitFlow } from 'src/utils';
import cancerStore, { CancerStore } from 'src/stores/CancerStore';
import { observer } from 'mobx-react-lite';
import InfoItem from 'src/components/InfoItem';
import { gaTrackEvent } from '../../api';
dayjs.extend(customParseFormat);

interface YourInfoObserverProps {
  cancerStore?: CancerStore;
}

const YourInfoObserver = observer(({ cancerStore }: YourInfoObserverProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const nationList = cancerStore.nationalitys;
  const [showingCalendar, setShowingCalendar] = useState(false);

  const [birthDate, setBirthDate] = useState(
    cancerStore?.formInfo?.birthDate
      ? cancerStore?.formInfo?.birthDate
      : UserInfoService.getUserInfo()?.dob != null &&
        isValidDate(UserInfoService.getUserInfo()?.dob, t('date_format2'))
      ? dayjs(UserInfoService.getUserInfo()?.dob, t('date_format2')).toDate()
      : null
  );
  const [errBirthDate, setErrBirthDate] = useState(null);
  const [gender, setGender] = useState(
    cancerStore?.formInfo?.gender || UserInfoService.getUserInfo()?.gender
  );
  const [errGender, setErrGender] = useState(null);
  const [nation, setNation] = useState(
    cancerStore?.formInfo?.nationality || VN_COUNTRY_CODE
  );
  const _handleChangeGender = (newGender) => {
    setGender(newGender);
    if (newGender) {
      setErrGender(null);
    }
  };

  const _handleChangeDay = useCallback((date) => {
    setBirthDate(date);
    setErrBirthDate(null);
  }, []);

  const _handleChangeNation = useCallback(
    (newNation) => {
      setNation(newNation);
    },
    [setNation]
  );

  const _getNationName = useCallback(() => {
    if (!nation) return null;
    return nationList.find((item) => item.id == nation)?.name || null;
  }, [nationList, nation]);

  const _handleContinue = () => {
    console.log('_handleContinue', birthDate);
    if (!birthDate) {
      setErrBirthDate(t('dob_required'));
      return;
    } else if (!gender) {
      setErrGender(t('gender_required'));
      return;
    } else if (nation != VN_COUNTRY_CODE) {
      PopupService.instance.current.open({
        visible: true,
        title: t('notification'),
        content: t('err_nation'),
        cancelTitle: t('later'),
        showContactButton: true,
        hotline: t('hotline2'),
        onOk: () => {
          PopupService.instance.current.close();
        },
        onCancel: () => {
          PopupService.instance.current.close();
        },
      });
      return;
    }
    cancerStore.fetchInsurancePackage(
      dayjs(birthDate).format(t('date_format1')),
      gender,
      _getNationName(),
      history
    );
    gaTrackEvent(BUYING_PROCESS_TRACKING,
      BUYING_PROCESS[BUYING_PROCESS_STEP.InputCustomerInfo].name,
      UserInfoService.getUserInfo()?.idCardNo ?? "");
  };

  useEffect(() => {
    cancerStore.cleanFormInfo();
    cancerStore.fetchCategory();
  }, [cancerStore]);

  const _handleClose = () => {
    confirmQuitFlow(history);
  };

  const _handleBack = () => {
    cancerStore.updateFormInfo({
      birthDate: null,
      gender: null,
      nationality: null,
    });
    history.goBack();
  };

  const _handleFocusDatePicker = () => {
    setShowingCalendar(true);
  };

  const _handleBlurDatePicker = () => {
    setShowingCalendar(false);
  };

  return (
    <div className="screen-container bg-background">
      <Toolbar
        title={t('your_info')}
        showClose={true}
        onClose={_handleClose}
        onBack={_handleBack}
      />
      <div className="max-w-md mx-auto screenScrollContainer">
        {/* Fix for iPhone 6,7,8, for this screen only */}
        {showingCalendar && window.innerHeight < 720 && (
          <div className={'h-52'} />
        )}
        <div className="px-4 py-5 labelText">
          {t('please_confirm_age_and_gender')}
        </div>
        <div className="bg-surface px-4 pt-6 pb-8">
          <div className="mb-5">
            {/* <TextInput
              disabled={true}
              label={t('full_name')}
              value={UserInfoService.getUserInfo()?.fullname || ''}
            /> */}

            <InfoItem
              title={t('full_name')}
              content={UserInfoService.getUserInfo()?.fullname || ''}
            />
          </div>
          <div className="mb-5">
            <DatePicker
              label={t('dob_full')}
              placeholder={t('date_format1')}
              value={birthDate}
              onChange={_handleChangeDay}
              isRequired={true}
              error={!!errBirthDate}
              helperText={errBirthDate}
              maxDate={new Date()}
              onFocus={_handleFocusDatePicker}
              onBlur={_handleBlurDatePicker}
            />
          </div>
          <div className="mb-5">
            <GenderSelector
              value={gender}
              onChange={_handleChangeGender}
              isRequired={true}
              helperText={errGender}
              error={!!errGender}
            />
          </div>
          <div className="mb-5">
            <NationSelector
              isRequired={true}
              value={nation}
              onChange={_handleChangeNation}
              nationList={cancerStore.nationalitys}
            />
          </div>
        </div>
      </div>
      <div className="bottomButtonBlock">
        <Button
          text={t('continue')}
          onClick={_handleContinue}
          rightIcon={<img src={forwardIcon} className="w-6 h-6 ml-2" />}
          extraClassName={'w-full'}
        />
      </div>
    </div>
  );
});

const YourInfo = () => <YourInfoObserver cancerStore={cancerStore} />;
export default YourInfo;
